/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

/* eslint-disable class-methods-use-this */

import NumberUtils from 'js-sdk/src/common/util/parse/NumberUtils';
import visitMultiAxisDatasetNode from '../../../../store/util/visitMultiAxisDatasetNode';
import { getEvents, getOutputParamsKeyFromField, getOutputParamFromMeasure } from '../util/EventUtil';
import {
    pieSeriesVisitor,
    getValue,
    getLabelToFieldMeasureMapping,
    getFieldToLabelMeasureMapping
} from '../util/GenericSeriesUtil';
import i18n from '../../../../../i18n/AdHocBundle.properties';
import GenericChartVisualization from '../GenericChartVisualization';

const numberUtils = new NumberUtils();
export default class GaugeGenericChartVisualization extends GenericChartVisualization {

    // eslint-disable-next-line class-methods-use-this
    getDrilldownEnabled(): boolean {
        return false;
    }

    getPlotOptionsSection() :any {
        return {
            series: {
                states: {
                    inactive: {
                        opacity: 1
                    }
                }
            }
        }
    }

    collectDataForCharts(chartUserSettings :any) {

        const dataset :any = this.getDataset();
        const staticProperties :any = this.getStaticProperties();
        const formattingOptions :any = this.getFormattingOptions();
        const linkOptions :any = this.getLinkOptions();

        const events :any = getEvents(linkOptions);
        const haveEvents :boolean = Object.keys(events).length > 0;
        const levels :any = staticProperties.levelsStructure;
        const selectedColIndex :number = this.getSelectedColumnIndex();
        const measureLabelToFieldMapping :any = getLabelToFieldMeasureMapping(levels);
        const measureFieldToLabelMapping :any = getFieldToLabelMeasureMapping(levels);
        const columnItems :any[] = dataset.summary.columns.items;
        const allFields :any[] = [];
        let measurePosition :number = 0;
        columnItems.forEach((item :any, index :number) => {
            if (item.measuresLevel === false) allFields.push({ ...item, position: allFields.length })
            else measurePosition = index;
        });
        const measuresLevel :any = levels.columns.find((level: any) => (level.kind === 'measures'));
        const amountOfMeasures :number = measuresLevel.measures.length;
        const amountOfFields :number = allFields.length;

        const series :any = visitMultiAxisDatasetNode(dataset.data.columns, pieSeriesVisitor, {
            result: [],
            stack: [],
            levels: levels.columns,
            measureFieldToLabelMapping,
            includeMeasureName: this.showMeasureName(),
            selectedIndex: selectedColIndex,
            skipTotals: dataset.summary.columns.items.length === selectedColIndex,
            includeEmptyLabels: true
        }).result;

        const convertValueToString = (value :number) => {

            let valueAsString :string = numberUtils.formatNumber(value, {
                decimalSeparator: '.',
                groupingSeparator: ','
            });

            // 'decimalPlaces' for a user is a text field, which he may left empty to indicate 'no rounding'
            // or can set it to some number which specifies how many decimal places he wants to have
            if (chartUserSettings.decimalPlaces !== '') {
                const decimalPlaces :number = Math.min(parseInt(chartUserSettings.decimalPlaces, 10), 20);
                valueAsString = numberUtils.formatNumber(value, {
                    decimalSeparator: '.',
                    groupingSeparator: ',',
                    decimalPlacesAmount: decimalPlaces
                });
            }

            return valueAsString;
        };

        const chartsData :any = [];
        if (selectedColIndex <= measurePosition) {

            for (let i = 0; i < series.length; i += 1) {
                const serie = series[i];
                for (let k = 0; k < measuresLevel.measures.length; k += 1) {

                    const fieldNames :any = [];
                    for (let j = 0; j < amountOfFields; j += 1) {
                        if (typeof serie.pathByFieldsAndMeasures[allFields[j].position] !== 'undefined') {
                            fieldNames.push(serie.pathByFieldsAndMeasures[allFields[j].position]);
                        } else {
                            fieldNames.push(i18n['adhoc.node.total.node']);
                        }
                    }

                    const measureName :string = measuresLevel.measures[k].label;

                    const value = getValue(dataset.data.aggregations, 0, serie.index + k);

                    const linkOutputParams :any = {};
                    if (haveEvents) {
                        linkOutputParams.measures = [getOutputParamFromMeasure(measuresLevel.measures[k])];
                        linkOutputParams.values = [];
                        for (let j = 0; j < amountOfFields; j += 1) {
                            const fieldName = serie.pathByFields[j];
                            linkOutputParams.values.push({
                                key: getOutputParamsKeyFromField(allFields[j]),
                                value: fieldName
                            });
                        }
                    }

                    chartsData.push({
                        fieldNames,
                        measureName,
                        value,
                        valueAsString: convertValueToString(value),
                        linkOutputParams
                    });
                }
            }

        } else {

            for (let i = 0; i < series.length; i += 1) {
                const serie = series[i];

                const fieldNames :any = [];
                for (let j = 0; j < amountOfFields; j += 1) {
                    if (typeof serie.pathByFields[allFields[j].position] !== 'undefined') {
                        fieldNames.push(serie.pathByFields[allFields[j].position]);
                    } else {
                        fieldNames.push(i18n['adhoc.node.total.node']);
                    }
                }

                const measureName :string = serie.pathByFieldsAndMeasures[measurePosition];

                const value = getValue(dataset.data.aggregations, 0, serie.index);

                const linkOutputParams :any = {};
                if (haveEvents) {
                    let measureNameForLinkOutput :string = serie.pathByFieldsAndMeasures[measurePosition];
                    if (measureLabelToFieldMapping[measureNameForLinkOutput]) {
                        measureNameForLinkOutput = measureLabelToFieldMapping[measureNameForLinkOutput];
                    }
                    linkOutputParams.measures = [measureNameForLinkOutput];
                    linkOutputParams.values = [];
                    for (let j = 0; j < amountOfFields; j += 1) {
                        const fieldName = serie.pathByFields[j];
                        linkOutputParams.values.push({
                            key: allFields[j].hierarchicalName,
                            value: fieldName
                        });
                    }
                }

                chartsData.push({
                    fieldNames,
                    measureName,
                    value,
                    valueAsString: convertValueToString(value),
                    linkOutputParams
                });
            }
        }

        if (!formattingOptions.showSingleMeasuresLabels && amountOfMeasures === 1) {
            chartsData.forEach((chartData :any) => {
                // eslint-disable-next-line no-param-reassign
                chartData.measureName = '';
            });
        }

        if (selectedColIndex === 0) {
            // if the column slider selector set to minimum position then let's take just one of the 'Totals'
            // labels because all of them are the same on this level of slider.
            // Important note: we are getting 'Totals' label form here because it will be i18n'ed, which is cool
            // and we don't need to translate word 'Totals'
            chartsData.forEach((chartData :any) => {
                if (chartData.fieldNames.length > 0) {
                    // eslint-disable-next-line no-param-reassign
                    chartData.fieldNames = [chartData.fieldNames[0]];
                }
            });
        }

        return chartsData;
    }

    groupDataByCharts(chartUserSettings :any) {

        const chartsData :any = this.collectDataForCharts(chartUserSettings);

        const groupedData :any = [];
        const map :any = {};

        chartsData.forEach((chartData :any) => {
            const groupKey = chartData.fieldNames.join('_');
            if (!map[groupKey]) {
                map[groupKey] = {
                    fieldNames: chartData.fieldNames,
                    measures: []
                };
                groupedData.push(map[groupKey]);
            }
            const measureInfo :any = {
                name: chartData.measureName,
                value: chartData.value,
                valueAsString: chartData.valueAsString,
                linkOutputParams: chartData.linkOutputParams
            };
            map[groupKey].measures.push(measureInfo);
        });

        return groupedData;
    }

    findAndRecordMinMaxValues(highchartsOptions :any) {

        let firstSet = false,
            min :number = 0,
            max :number = 0;

        for (let i = 0; i < highchartsOptions.series.length; i += 1) {
            const serie = highchartsOptions.series[i];

            for (let k = 0; k < serie.data.length; k += 1) {
                const value = serie.data[k].y;
                if (!firstSet) {
                    min = value;
                    max = value;
                    firstSet = true;
                } else {
                    min = Math.min(min, value);
                    max = Math.max(max, value);
                }
            }
        }

        super.recordMinMaxValues(min, max);
    }
}
